import { useMutation } from '@tanstack/react-query'
import {
  ArrowRight,
  CheckCircle2,
  FileSpreadsheet,
  RefreshCw,
  TriangleAlert,
  Upload,
  Users,
} from 'lucide-react'
import { useRef, useState, type ReactNode } from 'react'

import {
  Badge,
  Button,
  EmptyState,
  Page,
  PageHeader,
  Select,
  Separator,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { ImportProgress } from '../components/import-progress.tsx'
import {
  ApiError,
  applyPartyImport,
  previewPartyImport,
  type MarkerRule,
  type PartyImportPreview,
  type PartyImportResult,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * Trazer a carteira de clientes do sistema antigo (F033).
 *
 * A tela é a irmã de `importar.tsx` e tem a mesma premissa — nada é gravado
 * antes de os números aparecerem — com um passo a mais, que é o motivo desta
 * feature existir separada.
 *
 * ## O passo dos marcadores
 *
 * A planilha do GDOOR traz o significado escondido na pontuação do nome. O dono
 * sabe o que `***` quer dizer: cliente que roubou dentro da loja. Ninguém sabe
 * mais o que `--` quis dizer, e são 795 pessoas.
 *
 * Um importador comum faria uma de duas coisas ruins: apagar a pontuação e
 * perder a informação, ou adivinhar. Este pergunta. Cada marcador aparece com
 * **quantas pessoas ele atinge**, três exemplos de verdade, e uma escolha. A
 * contagem é grande de propósito: arquivar 795 clientes tem de parecer o que é.
 *
 * E o marcador fica gravado nas observações de quem o carregava, mesmo quando a
 * regra é ignorá-lo — para que a resposta sobre o `--` possa vir daqui a um mês
 * sem depender de alguém ainda ter o arquivo.
 */

const PROBLEM_LABELS: Record<string, string> = {
  'system-customer': 'Consumidor genérico do sistema antigo',
  'missing-name': 'Linha sem nome',
  'missing-code': 'Linha sem código',
  'duplicate-code': 'Código repetido na própria planilha',
  'duplicate-document':
    'CPF/CNPJ que já é de outro cadastro — o cliente entra sem o documento',
  'split-uncertain': 'Nome que não deu para separar em pessoa e loja',
  'repaired-phone': 'Celular antigo — o nono dígito foi acrescentado',
  'unreadable-phone': 'Telefone que não pôde ser lido',
  'no-phone': 'Sem telefone nenhum',
}

/** Problemas que impedem a linha, em oposição aos que só são relatados. */
const BLOCKING = new Set([
  'system-customer',
  'missing-name',
  'missing-code',
  'duplicate-code',
])

const MARKER_LABELS: Record<MarkerRule, string> = {
  ignore: 'Só tirar a marca do nome',
  block: 'Bloquear o crédito deste cliente',
  archive: 'Arquivar o cliente',
}

const Count = ({
  value,
  label,
  detail,
  tone,
}: {
  value: number
  label: string
  detail: string
  tone: 'brand' | 'warning' | 'muted'
}): ReactNode => (
  <div className="flex-1 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-4">
    <p
      className={`text-(length:--text-2xl) font-(--weight-semibold) ${
        tone === 'brand'
          ? 'text-(--brand-primary)'
          : tone === 'warning'
            ? 'text-(--warning)'
            : 'text-(--text-secondary)'
      }`}
    >
      {value.toLocaleString('pt-BR')}
    </p>
    <p className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
      {label}
    </p>
    <p className="text-(length:--text-xs) text-(--text-secondary)">{detail}</p>
  </div>
)

export const ImportarClientesPage = (): ReactNode => {
  const { allows } = useAuth()
  const canWrite = allows('party.write')

  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<PartyImportPreview | null>(null)
  const [result, setResult] = useState<PartyImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)

  /**
   * O que fazer com cada marcador, e a resposta de cada linha ambígua.
   *
   * `***` já vem escolhido como bloqueio — é a única regra que alguém explicou.
   * Todo o resto começa em "só tirar a marca", que é a única coisa que se pode
   * fazer com uma convenção que ninguém sabe ler.
   */
  const [rules, setRules] = useState<Record<string, MarkerRule>>({})
  const [answers, setAnswers] = useState<Set<number>>(new Set())

  const decisionsFor = (
    markerRules: Record<string, MarkerRule>,
    same: Set<number>
  ): { markerRules: Record<string, MarkerRule>; treatAsSame: number[] } => ({
    markerRules,
    treatAsSame: [...same],
  })

  const read = useMutation({
    mutationFn: async (input: {
      chosen: File
      markerRules: Record<string, MarkerRule>
    }) =>
      await previewPartyImport(
        input.chosen,
        decisionsFor(input.markerRules, new Set())
      ),
    onSuccess: (data, variables) => {
      setPreview(data)
      setResult(null)
      setError(null)

      /*
       * O `***` vem pré-escolhido, uma única vez.
       *
       * Só na primeira leitura, quando ainda não há regra nenhuma: reler a
       * planilha depois de alguém mudar a escolha não pode desfazer a escolha
       * que a pessoa acabou de fazer.
       */
      if (Object.keys(variables.markerRules).length === 0) {
        const seeded: Record<string, MarkerRule> = {}
        for (const entry of data.markers) {
          if (entry.marker.startsWith('*')) seeded[entry.marker] = 'block'
        }
        setRules(seeded)
        if (Object.keys(seeded).length > 0) {
          read.mutate({ chosen: variables.chosen, markerRules: seeded })
        }
      }
    },
    onError: (failure: unknown) => {
      setPreview(null)
      setError(
        failure instanceof ApiError
          ? failure.message
          : 'Não foi possível ler a planilha.'
      )
    },
  })

  const apply = useMutation({
    mutationFn: async (chosen: File) =>
      await applyPartyImport(chosen, decisionsFor(rules, answers)),
    onSuccess: (data) => {
      setResult(data)
      setError(null)
    },
    onError: (failure: unknown) => {
      setError(
        failure instanceof ApiError
          ? failure.message
          : 'A importação não pôde ser concluída.'
      )
    },
  })

  const choose = (chosen: File | null): void => {
    if (chosen === null) return
    setFile(chosen)
    setResult(null)
    setRules({})
    setAnswers(new Set())
    read.mutate({ chosen, markerRules: {} })
  }

  /** Trocar uma regra relê a planilha: as contagens têm de acompanhar. */
  const setRule = (marker: string, rule: MarkerRule): void => {
    const next = { ...rules, [marker]: rule }
    setRules(next)
    if (file !== null) read.mutate({ chosen: file, markerRules: next })
  }

  const restart = (): void => {
    setFile(null)
    setPreview(null)
    setResult(null)
    setError(null)
    setRules({})
    setAnswers(new Set())
    if (inputRef.current !== null) inputRef.current.value = ''
  }

  const willImport =
    preview === null
      ? 0
      : preview.counts.create + preview.counts.update + answers.size

  const unanswered = preview === null ? 0 : preview.counts.review - answers.size

  return (
    <Page>
      <PageHeader
        title="Importar clientes"
        description="Envie a planilha de clientes exportada do seu sistema atual. Nada é gravado até você ver o que vai acontecer."
      />

      {!canWrite && (
        <div
          role="status"
          className="rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-4 text-(length:--text-sm) text-(--text-secondary)"
        >
          Você não tem permissão para alterar cadastros, então esta tela é só
          leitura.
        </div>
      )}

      {result === null && (
        <section
          onDragOver={(event) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => {
            setDragging(false)
          }}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            choose(event.dataTransfer.files.item(0))
          }}
          className={`flex flex-col items-center gap-3 rounded-(--radius-md) border-2 border-dashed p-8 text-center transition-colors ${
            dragging
              ? 'border-(--brand-primary) bg-(--surface-raised)'
              : 'border-(--border-subtle)'
          }`}
        >
          <FileSpreadsheet
            aria-hidden="true"
            className="h-8 w-8 text-(--text-muted)"
          />
          <div>
            <p className="text-(length:--text-md) font-(--weight-medium) text-(--text-primary)">
              {file === null
                ? 'Arraste a planilha aqui'
                : `${file.name} · ${(file.size / 1024).toFixed(0)} KB`}
            </p>
            <p className="text-(length:--text-sm) text-(--text-secondary)">
              Arquivo .xlsx do GDOOR (Código, Nome do cliente, Celular,
              Telefone) ou a planilha com Código do Cliente, Nome/Razão Social,
              telefones, Documento e Vendedores
            </p>
          </div>

          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            className="sr-only"
            id="import-parties-file"
            onChange={(event) => {
              choose(event.target.files?.item(0) ?? null)
            }}
          />
          <Button
            type="button"
            variant="secondary"
            leadingIcon={<Upload aria-hidden="true" className="h-4 w-4" />}
            onClick={() => inputRef.current?.click()}
          >
            Escolher arquivo
          </Button>
        </section>
      )}

      {read.isPending && (
        <div className="flex items-center justify-center gap-3 py-8">
          <Spinner />
          <span className="text-(length:--text-sm) text-(--text-secondary)">
            Lendo a planilha…
          </span>
        </div>
      )}

      {error !== null && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) p-4"
        >
          <TriangleAlert
            aria-hidden="true"
            className="mt-0.5 h-4 w-4 shrink-0 text-(--danger)"
          />
          <p className="text-(length:--text-sm) text-(--danger)">{error}</p>
        </div>
      )}

      {apply.isPending && <ImportProgress rows={willImport} noun="clientes" />}

      {preview !== null && result === null && (
        <>
          <section className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Badge tone="info">{preview.profile.label}</Badge>
              <span className="text-(length:--text-sm) text-(--text-secondary)">
                {preview.rowCount.toLocaleString('pt-BR')} linhas ·{' '}
                {preview.header.join(' · ')}
              </span>
            </div>

            <div className="flex flex-wrap gap-3">
              <Count
                value={preview.counts.create}
                label="Vão entrar"
                detail="clientes novos"
                tone="brand"
              />
              <Count
                value={preview.counts.update}
                label="Vão ser atualizados"
                detail="já existem aqui"
                tone="muted"
              />
              <Count
                value={preview.counts.skip}
                label="Não vão entrar"
                detail="veja os motivos abaixo"
                tone="muted"
              />
            </div>
          </section>

          {/* -------------------------------------------------------------- */}
          {/* Os marcadores: a pergunta que só esta tela faz                  */}
          {/* -------------------------------------------------------------- */}
          {preview.markers.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
                Marcas encontradas no nome
              </h2>
              <p className="text-(length:--text-sm) text-(--text-secondary)">
                O sistema antigo guardava recado na pontuação antes do nome.
                Diga o que cada marca significa — ela sai do nome de qualquer
                jeito, e fica registrada nas observações do cliente para você
                poder mudar de ideia depois.
              </p>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell className="w-20">Marca</TableHeaderCell>
                      <TableHeaderCell className="w-24 text-right">
                        Clientes
                      </TableHeaderCell>
                      <TableHeaderCell>Exemplos</TableHeaderCell>
                      <TableHeaderCell className="w-64">
                        O que fazer
                      </TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.markers.map((entry) => (
                      <TableRow key={entry.marker}>
                        <TableCell className="font-mono text-(--text-primary)">
                          {entry.marker}
                        </TableCell>
                        <TableCell className="text-right font-(--weight-semibold) text-(--text-primary)">
                          {entry.count.toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-(length:--text-xs) text-(--text-muted)">
                          {entry.sample.join(' · ')}
                        </TableCell>
                        <TableCell>
                          <Select
                            aria-label={`O que fazer com a marca ${entry.marker}`}
                            value={rules[entry.marker] ?? 'ignore'}
                            disabled={!canWrite || read.isPending}
                            onChange={(event) => {
                              setRule(
                                entry.marker,
                                event.target.value as MarkerRule
                              )
                            }}
                          >
                            {(['ignore', 'block', 'archive'] as const).map(
                              (rule) => (
                                <option key={rule} value={rule}>
                                  {MARKER_LABELS[rule]}
                                </option>
                              )
                            )}
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {(preview.blocked > 0 || preview.archived > 0) && (
                <div
                  role="status"
                  className="flex items-start gap-3 rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) p-4"
                >
                  <TriangleAlert
                    aria-hidden="true"
                    className="mt-0.5 h-4 w-4 shrink-0 text-(--warning)"
                  />
                  <p className="text-(length:--text-sm) text-(--warning)">
                    {preview.blocked > 0 && (
                      <>
                        <strong>
                          {preview.blocked.toLocaleString('pt-BR')}
                        </strong>{' '}
                        {preview.blocked === 1
                          ? 'cliente entra com o crédito bloqueado'
                          : 'clientes entram com o crédito bloqueado'}
                        : não levam fiado, e quem atender é avisado no PDV.{' '}
                      </>
                    )}
                    {preview.archived > 0 && (
                      <>
                        <strong>
                          {preview.archived.toLocaleString('pt-BR')}
                        </strong>{' '}
                        {preview.archived === 1
                          ? 'cliente entra arquivado'
                          : 'clientes entram arquivados'}
                        : não aparecem na busca do dia a dia.
                      </>
                    )}
                  </p>
                </div>
              )}
            </section>
          )}

          {preview.sellers !== undefined && preview.sellers.length > 0 && (
            <section
              aria-label="Vendedores da planilha"
              className="flex flex-col gap-2"
            >
              <h2 className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
                A carteira de cada vendedor
              </h2>
              <p className="text-(length:--text-sm) text-(--text-secondary)">
                O vendedor da planilha vira o vendedor do cliente, e a conversa
                de WhatsApp dele cai para essa pessoa. Quem não é usuário do
                Aeris fica sem vendedor — o gerente distribui depois.
              </p>
              <TableContainer>
                <Table>
                  <TableBody>
                    {preview.sellers.map((entry) => (
                      <TableRow key={entry.name}>
                        <TableCell className="w-20 text-right font-(--weight-semibold)">
                          {entry.count.toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-(--text-primary)">
                          {entry.name}
                        </TableCell>
                        <TableCell className="w-44 text-right">
                          <Badge
                            tone={entry.userId === null ? 'warning' : 'success'}
                          >
                            {entry.userId === null
                              ? 'não é usuário do Aeris'
                              : 'achado'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </section>
          )}

          {preview.problems.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
                O que a planilha traz de estranho
              </h2>
              <TableContainer>
                <Table>
                  <TableBody>
                    {preview.problems.map((entry) => (
                      <TableRow key={entry.problem}>
                        <TableCell className="w-20 text-right font-(--weight-semibold)">
                          {entry.count.toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell>
                          <p className="text-(--text-primary)">
                            {PROBLEM_LABELS[entry.problem] ?? entry.problem}
                          </p>
                          <p className="text-(length:--text-xs) text-(--text-muted)">
                            {entry.sample.join(' · ')}
                          </p>
                        </TableCell>
                        <TableCell className="w-32 text-right">
                          <Badge
                            tone={
                              BLOCKING.has(entry.problem)
                                ? 'warning'
                                : 'neutral'
                            }
                          >
                            {BLOCKING.has(entry.problem)
                              ? 'não entra'
                              : 'entra assim'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </section>
          )}

          {preview.review.length > 0 && (
            <section className="flex flex-col gap-2">
              <h2 className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
                {preview.review.length} códigos que preciso que você olhe
              </h2>
              <p className="text-(length:--text-sm) text-(--text-secondary)">
                Este código já existe aqui, mas com outro nome. É a mesma
                pessoa, que mudou de nome ou de loja? Ou o sistema antigo
                entregou o código a outra pessoa? Sem resposta, a linha fica de
                fora.
              </p>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell className="w-16">Cód.</TableHeaderCell>
                      <TableHeaderCell>Era</TableHeaderCell>
                      <TableHeaderCell>Virou</TableHeaderCell>
                      <TableHeaderCell className="w-56">
                        É a mesma pessoa?
                      </TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.review.map((row) => (
                      <TableRow key={row.line}>
                        <TableCell className="text-(--text-muted)">
                          {row.externalCode}
                        </TableCell>
                        <TableCell className="text-(--text-secondary)">
                          {row.previousName}
                        </TableCell>
                        <TableCell className="text-(--text-primary)">
                          {row.legalName}
                        </TableCell>
                        <TableCell>
                          <Select
                            aria-label={`Código ${row.externalCode}`}
                            value={answers.has(row.line) ? 'same' : ''}
                            disabled={!canWrite}
                            onChange={(event) => {
                              const next = new Set(answers)
                              if (event.target.value === 'same')
                                next.add(row.line)
                              else next.delete(row.line)
                              setAnswers(next)
                            }}
                          >
                            <option value="">Não importar</option>
                            <option value="same">
                              Sim, atualizar este cliente
                            </option>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </section>
          )}

          <Separator />

          <section className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-(length:--text-sm) text-(--text-primary)">
                <strong>{willImport.toLocaleString('pt-BR')}</strong> clientes
                serão gravados.
              </p>
              {unanswered > 0 && (
                <p className="text-(length:--text-xs) text-(--text-secondary)">
                  {unanswered.toLocaleString('pt-BR')} códigos sem resposta
                  ficam de fora — você pode importar agora e resolvê-los depois.
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={restart}>
                Trocar arquivo
              </Button>
              <Button
                type="button"
                disabled={
                  !canWrite ||
                  apply.isPending ||
                  read.isPending ||
                  file === null
                }
                leadingIcon={
                  apply.isPending ? (
                    <Spinner className="h-4 w-4" />
                  ) : (
                    <ArrowRight aria-hidden="true" className="h-4 w-4" />
                  )
                }
                onClick={() => {
                  if (file !== null) apply.mutate(file)
                }}
              >
                {apply.isPending ? 'Importando…' : 'Importar'}
              </Button>
            </div>
          </section>
        </>
      )}

      {result !== null && (
        <section
          aria-label="Importação concluída"
          className="flex flex-col gap-4 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-raised) p-6"
        >
          <div className="flex items-center gap-2">
            <CheckCircle2
              aria-hidden="true"
              className="h-5 w-5 text-(--success)"
            />
            <h2 className="text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
              Importação concluída
            </h2>
          </div>

          <div className="flex flex-wrap gap-3">
            <Count
              value={result.created}
              label="Criados"
              detail="clientes novos"
              tone="brand"
            />
            <Count
              value={result.updated}
              label="Atualizados"
              detail="já existiam"
              tone="muted"
            />
            <Count
              value={result.contacts}
              label="Telefones"
              detail="gravados nos contatos"
              tone="muted"
            />
            <Count
              value={result.blocked}
              label="Bloqueados"
              detail="não levam fiado"
              tone="warning"
            />
          </div>

          {result.unanswered > 0 && (
            <p className="text-(length:--text-sm) text-(--text-secondary)">
              {result.unanswered.toLocaleString('pt-BR')} códigos ficaram de
              fora porque não foram respondidos. Envie a planilha de novo para
              resolvê-los — quem já entrou não será duplicado.
            </p>
          )}

          <Button
            type="button"
            variant="secondary"
            leadingIcon={<RefreshCw aria-hidden="true" className="h-4 w-4" />}
            onClick={restart}
          >
            Importar outra planilha
          </Button>
        </section>
      )}

      {preview === null && result === null && !read.isPending && (
        <EmptyState
          icon={<Users aria-hidden="true" className="h-6 w-6" />}
          title="Nenhuma planilha ainda"
          description="A importação cria os clientes com nome, nome fantasia e telefone. Endereço e e-mail não vêm nesta planilha e continuam sendo cadastrados aqui."
        />
      )}
    </Page>
  )
}
